import styled, { WithTheme } from '@emotion/styled';
import { IconPosition, StyledProps as Props, Theme } from './Input.types';
import { css } from '@emotion/core';
import { SerializedStyles } from '@emotion/serialize';
import { variant } from 'styled-system';

export const getStateColors = () => ({
  default: '',
  success: 'var(--color-green)',
  error: 'var(--color-coral)',
  warning: 'var(--color-honey)',
});

const base = (p: WithTheme<Props, Theme>): SerializedStyles =>
  css({
    lineHeight: p.theme.lineHeights[7],
    padding: '0.625em 6px',
    background: 'var(--color-neutral-5)',
    borderRadius: '4px',
    border: '1px solid var(--color-neutral-0)',
    outline: 'none',
    color: 'var(--color-smoke)',
    width: '100%',
    '&:hover': {
      border: `1px solid var(--color-neutral-15)`,
    },
    '&:focus-within': {
      border: `1px solid var(--color-honey-50)`,
      background: 'var(--color-neutral-5)',
      boxShadow: `0 0 0 2px var(--color-honey-30)`,
    },
  });

const filled = (p: WithTheme<Props, Theme>): SerializedStyles =>
  p.value
    ? css({
        color: 'var(--color-smoke)',
        borderColor: 'var(--color-smoke-50)',
      })
    : null;

const states = (p: WithTheme<Props, Theme>): SerializedStyles =>
  variant({
    prop: 'state',
    variants: {
      success: {
        borderColor: 'var(--color-green)',
      },
      error: {
        borderColor: 'var(--color-coral)',
      },
      warning: {
        borderColor: 'var(--color-honey)',
      },
      filled: {
        color: 'var(--color-smoke)',
        borderColor: 'var(--color-smoke-50)',
      },
    },
  });

export const InputContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex: 1 0;
  align-items: center;
  padding-top: 2px;
  min-width: 0;
`;

export const TextInput = styled.input`
  flex: 1 0;
  min-width: 20px;
  min-height: 28px;
  font-size: inherit;
  background-color: transparent;
  border: 0;
  padding-left: 6px;

  &:focus {
    outline: none;
  }

  &:hover {
    background-color: transparent;
  }

  &[disabled]:hover {
    background-color: transparent;
  }

  ${(p: WithTheme<Props, Theme>) =>
    css({
      lineHeight: p.theme.lineHeights[7],
      fontFamily: p.theme.fonts.secondary,
      fontStyle: 'normal',
      fontWeight: 'normal',
      fontSize: p.theme.sizes.font.base,
      color: 'var(--color-smoke)',
      '&::placeholder': {
        color: 'var(--color-smoke-50)',
      },
      '&:disabled': {
        opacity: 1,
        WebkitTextFillColor: 'var(--color-smoke-30)',
        backgroundColor: 'transparent',
        color: 'var(--color-smoke-30)',
      },
    })}
`;

export const Icon = styled.div<any>`
  flex: 0 0;
  display: flex;
  align-items: center;

  ${(p) =>
    css({
      padding: p.iconPosition === IconPosition.RIGHT ? '0 6px 0 0' : '0 0 0 6px',
    })}
`;

export const Container = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-height: 50px;
  
  ${base}

  ${filled}

  ${states}

  ${(p) =>
    p.disabled
      ? css({
          border: '1px solid var(--color-neutral-15)',
          background: 'var(--color-neutral-15)',
          '&:hover': {
            border: '1px solid var(--color-neutral-15)',
          },
        })
      : null}
`;

export default styled.input<Props, Theme>`
  ${base}

  ${filled}

  ${states}
`;
